import { RefObject } from 'react';
import { Col, Divider, Row } from 'antd';
import { isNull } from 'lodash';
import { useAppSelector } from 'hooks/redux';
import { CoverageDetails, IndividualName } from 'modules/auth/types/types';
import { formatEffectiveDates } from 'utils/commonUtil';
import { getCardPlanYear } from 'modules/auth/util/idCardPlanYears';
import { getText } from 'utils/stringUtil';
import {
  EFFECTIVE_DATE,
  ID_CARD_NAME,
  ID_CARD_PRIMARY_SUBSCRIBER,
  MEMBER_ID,
  ID_CARD_GROUP_ID,
  ID_CARD_PHONE,
  ID_CARD_RX_BIN,
  ID_CARD_RX_PCN,
  ID_CARD_RX_GROUP,
  ID_CARD_MEMBERS_AND_PROVIDERS,
  NETWORK_PROVIDERS_MUST_CALL,
  ID_CARD_CARRIER_PHONE,
} from 'constants/languageConstants';
import { PLAN_VIEW } from 'modules/auth/constants/authConstants';
import OverflowPopover from 'components/PlanYearOverFlowPopover/OverflowPopover';

import styles from './idCardPrint.module.less';

type IDDataType = {
  id: string;
  benefitKind: string;
  planId: string;
  planName: string;
  individualId: string;
  subscriberId: string;
  employerId: string;
  individualName: IndividualName;
  subscriberName: IndividualName;
  dob: string | null;
  coverageDetails: CoverageDetails;
  tier: string | null;
  planYearId: string;
};

type IDType = {
  isFlipped: boolean;
  data: IDDataType;
  employerData: any;
  IdRef: RefObject<any>;
  view: string;
};

const IDCardPrint = ({ data, employerData, IdRef, view }: IDType) => {
  const language = useAppSelector((state) => state.benguide.data?.language);
  const currentPlanYear = useAppSelector(
    (state) => state.idCard.currentPlanYear
  );
  const planYearsById = useAppSelector((state) => state.idCard.planYearsById);
  const cardPlanYear = getCardPlanYear(
    planYearsById,
    data?.planYearId,
    currentPlanYear
  );
  const getPlanYearDates = formatEffectiveDates(
    cardPlanYear?.startDate,
    cardPlanYear?.endDate
  );
  return (
    <div className={styles.wrapperContainer} ref={IdRef}>
      <Row justify="start">
        <h1 className={styles.heading}>
          {`${data?.individualName?.firstName} ${data?.individualName?.lastName}`}{' '}
          :
        </h1>
      </Row>
      <Row>
        <Col span={4}>
          <h2 className={styles.subHeading}>
            {PLAN_VIEW.MEDICAL.type === view
              ? getText(PLAN_VIEW.MEDICAL.value, language)
              : PLAN_VIEW.DENTAL.type === view
              ? getText(PLAN_VIEW.DENTAL.value, language)
              : getText(PLAN_VIEW.VISION.value, language)}
          </h2>
        </Col>
        <Col>
          <div className={styles.flipCardFront}>
            <div className={styles.cardTopContainer}>
              <Row justify="space-between">
                <Col>
                  {data?.coverageDetails?.logoUrl && (
                    <img
                      src={data?.coverageDetails?.logoUrl}
                      className={styles.employerLogo}
                      alt="employer"
                    />
                  )}
                </Col>
                <Col>
                  {employerData?.logoUrl && (
                    <img
                      src={employerData?.logoUrl}
                      className={styles.employerLogo}
                      alt="client"
                    />
                  )}
                </Col>
              </Row>
              <h1 className={styles.heading}>
                <OverflowPopover maxWidth="490px">
                  {data?.planName}
                </OverflowPopover>
              </h1>

              <p className={styles.subHeading}>
                <b>
                  {' '}
                  {getText(EFFECTIVE_DATE, language)} :{' '}
                  {`${getPlanYearDates?.startDate}`}{' '}
                </b>
              </p>
            </div>
            <div className={styles.cardBottomContainer}>
              <Row gutter={24}>
                <Col span={12} className={styles.labelText}>
                  <b>{getText(ID_CARD_NAME, language)}</b>
                </Col>
                <Col span={12}>
                  <div className={styles.printableData}>
                    {data?.individualName?.firstName &&
                    data?.individualName?.lastName ? (
                      <>
                        {`${data?.individualName?.firstName} ${data?.individualName?.lastName}`}
                      </>
                    ) : (
                      '-'
                    )}
                  </div>
                </Col>
              </Row>
              <Row gutter={24}>
                <Col span={12} className={styles.labelText}>
                  <b>{getText(ID_CARD_PRIMARY_SUBSCRIBER, language)}</b>
                </Col>
                <Col span={12}>
                  <div className={styles.printableData}>
                    {data?.subscriberName?.firstName &&
                    data?.subscriberName?.lastName ? (
                      <>
                        {`${data?.subscriberName?.firstName} ${data?.subscriberName?.lastName}`}
                      </>
                    ) : (
                      '-'
                    )}
                  </div>
                </Col>
              </Row>
              <Divider className={styles.containerDivider} />
              <Row>
                <Col span={24}>
                  <Row>
                    <Col span={4} className={styles.labelText}>
                      <b>{getText(MEMBER_ID, language)}</b>
                    </Col>
                    <Col span={9}>
                      <div className={styles.editcontainer}>
                        <div className={styles.printableData}>
                          {data?.coverageDetails?.memberIdNumber ? (
                            <>
                              {`${
                                view === PLAN_VIEW.MEDICAL.type &&
                                !isNull(data?.coverageDetails?.alphaPrefix)
                                  ? data?.coverageDetails?.alphaPrefix
                                  : ''
                              }${data?.coverageDetails?.memberIdNumber}`}{' '}
                            </>
                          ) : (
                            '-'
                          )}
                        </div>
                      </div>
                    </Col>
                    {view === PLAN_VIEW.MEDICAL.type && (
                      <>
                        <Col span={4} className={styles.labelText}>
                          <b>{getText(ID_CARD_RX_BIN, language)}</b>
                        </Col>
                        <Col span={7}>
                          {data?.coverageDetails?.rxBINumber ? (
                            <div className={styles.printableData}>
                              {data?.coverageDetails?.rxBINumber}
                            </div>
                          ) : (
                            '-'
                          )}
                        </Col>
                      </>
                    )}
                  </Row>
                </Col>
              </Row>
              <Row>
                <Col span={24}>
                  <Row>
                    <Col span={4} className={styles.labelText}>
                      <b>{getText(ID_CARD_GROUP_ID, language)}</b>
                    </Col>
                    <Col span={9}>
                      {data?.coverageDetails?.groupIdNumber ? (
                        <div className={styles.printableData}>
                          {data?.coverageDetails?.groupIdNumber}
                        </div>
                      ) : (
                        '-'
                      )}
                    </Col>
                    {view === PLAN_VIEW.MEDICAL.type && (
                      <>
                        <Col span={4} className={styles.labelText}>
                          <b>{getText(ID_CARD_RX_PCN, language)}</b>
                        </Col>
                        <Col span={7}>
                          {data?.coverageDetails?.rxBINumber ? (
                            <div className={styles.printableData}>
                              {data?.coverageDetails?.rxBINumber}
                            </div>
                          ) : (
                            '-'
                          )}
                        </Col>
                      </>
                    )}
                  </Row>
                </Col>
              </Row>
              <Row>
                <Col span={24}>
                  <Row>
                    <Col span={4} className={styles.labelText}>
                      <b>{getText(ID_CARD_PHONE, language)}</b>
                    </Col>
                    <Col span={9}>
                      {data?.coverageDetails?.carrierPhone ? (
                        <div className={styles.printableData}>
                          <a
                            className={styles.linkText}
                            href={`tel:${data?.coverageDetails?.carrierPhone}`}
                          >
                            <b> {data?.coverageDetails?.carrierPhone}</b>
                          </a>
                        </div>
                      ) : (
                        '-'
                      )}
                    </Col>
                    {view === PLAN_VIEW.MEDICAL.type && (
                      <>
                        <Col span={4} className={styles.labelText}>
                          <b>{getText(ID_CARD_RX_GROUP, language)}</b>
                        </Col>
                        <Col span={7}>
                          {data?.coverageDetails?.rxGroupNumber ? (
                            <div className={styles.printableData}>
                              {data?.coverageDetails?.rxGroupNumber}
                            </div>
                          ) : (
                            '-'
                          )}
                        </Col>
                      </>
                    )}
                  </Row>
                </Col>
              </Row>
            </div>
          </div>
          <div className={styles.flipCardBack}>
            <div className={styles.cardTopContainer}>
              <Row justify="space-between">
                <Col>
                  {data?.coverageDetails?.logoUrl && (
                    <img
                      src={data?.coverageDetails?.logoUrl}
                      className={styles.employerLogo}
                      alt="employer"
                    />
                  )}
                </Col>
                <Col>
                  {employerData?.logoUrl && (
                    <img
                      src={employerData?.logoUrl}
                      className={styles.employerLogo}
                      alt="client"
                    />
                  )}
                </Col>
              </Row>
            </div>{' '}
            <div className={styles.cardBackBottomContainer}>
              <Row>
                <Col span={24}>
                  <h3 className={styles.backHeader}>
                    {getText(ID_CARD_MEMBERS_AND_PROVIDERS, language)}
                  </h3>
                  <span className={styles.subHeading}>
                    {getText(NETWORK_PROVIDERS_MUST_CALL, language)}
                  </span>
                </Col>
              </Row>
              <Divider className={styles.containerDivider} />

              <Row gutter={32} justify="space-between">
                <Col span={12} className={styles.labelText}>
                  <Row justify="space-between">
                    <b>{getText(ID_CARD_CARRIER_PHONE, language)}:</b>
                    {!data?.coverageDetails?.carrierPhone && '-'}
                  </Row>
                </Col>
                <Col>
                  {data?.coverageDetails?.carrierPhone && (
                    <a
                      className={styles.linkText}
                      href={`tel:${data?.coverageDetails?.carrierPhone}`}
                    >
                      <b> {data?.coverageDetails?.carrierPhone}</b>
                    </a>
                  )}
                </Col>
              </Row>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default IDCardPrint;
